<template>
  <div class="page-container">
    <!-- Full page background -->
    <div class="full-page-background">
      <img
        src="/img/homepage_bg.webp"
        alt="Background"
        class="background-image"
      />
    </div>

    <!-- Purple header section -->
    <div class="header-section">
      <!-- <header-bar></header-bar> -->
      <div class="header-content">
        <h1 class="header-title">Which PrEP Method is Right for You?</h1>
        <p class="header-subtitle">
          Before taking the quiz, learn more about the different methods:
        </p>
        <div v-if="heroMethods.length > 0" class="hero-method-row">
          <div
            v-for="method in heroMethods"
            :key="method.id"
            class="hero-method-chip"
            role="button"
            tabindex="0"
            :aria-label="'Jump to ' + method.label"
            @click="openMethod(method.id)"
            @keydown.enter="openMethod(method.id)"
            @keydown.space.prevent="openMethod(method.id)"
          >
            <div class="hero-method-icon-wrap">
              <img
                v-if="method.imageUrl"
                :src="method.imageUrl"
                :alt="method.label"
                class="hero-method-icon"
              />
            </div>
            <p class="hero-method-label">{{ method.label }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Light blue content section -->
    <div class="content-section">
      <div class="content-inner">
        <!-- Method selection buttons with individual expansions -->
        <div class="method-buttons">
          <div
            v-for="method in mainMethods"
            :key="method.name"
            class="method-item"
            :data-method-id="method.id"
          >
            <v-btn
              :class="[
                'method-btn',
                { active: selectedMethodId === method.id },
              ]"
              @click="selectMethod(method.id)"
              variant="flat"
              size="large"
              block
            >
              <div
                class="d-flex flex-row align-center justify-space-between w-100"
              >
                <div class="d-flex flex-row align-center method-name-group">
                  <div class="method-icon-chip">
                    <img
                      v-if="method.icon"
                      :src="method.icon"
                      :alt="method.name"
                      class="method-btn-icon"
                    />
                  </div>
                  <span class="method-text">{{ method.name }}</span>
                </div>
                <v-icon class="dropdown-icon">mdi-chevron-down</v-icon>
              </div>
            </v-btn>

            <!-- Expandable content for this specific method -->
            <transition name="method-expand">
              <div v-if="selectedMethodId === method.id" class="method-content">
                <div
                  v-for="(individualMethod, methodIndex) in method.methods"
                  :key="methodIndex"
                  :class="[
                    'method-details-box',
                    `method-card-${method.id
                      .toLowerCase()
                      .replace(/\s+/g, '-')}`,
                  ]"
                >
                  <div class="method-blurb-item">
                    <p v-if="individualMethod.tagline" class="method-tagline">
                      {{ individualMethod.tagline }}
                    </p>
                    <p v-if="individualMethod.schedule" class="method-schedule">
                      {{ individualMethod.schedule }}
                    </p>
                    <template v-if="sortedProcess(individualMethod).length > 0">
                      <p class="method-process-label">The process:</p>
                      <div class="method-process-list">
                        <div
                          v-for="(step, stepIndex) in sortedProcess(
                            individualMethod,
                          )"
                          :key="stepIndex"
                          class="method-process-row"
                        >
                          <span class="method-process-time">{{
                            step.time
                          }}</span>
                          <span class="method-process-detail">{{
                            step.detail
                          }}</span>
                        </div>
                      </div>
                    </template>
                    <p v-if="individualMethod.note" class="method-note">
                      {{ individualMethod.note }}
                    </p>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Loading state -->
          <div v-if="allMethods.length === 0" class="loading-container">
            <v-progress-circular
              indeterminate
              color="#eb63eb"
              size="40"
            ></v-progress-circular>
          </div>

          <!-- Quiz button at the bottom of scrollable content -->
        </div>
      </div>
    </div>

    <!-- Fixed bottom button -->
    <div class="fixed-bottom-buttons">
      <v-app-bar location="bottom" class="bottom-bar">
        <div class="d-flex flex-row align-center justify-center w-100">
          <v-btn
            class="quiz-button"
            size="x-large"
            @click="navigateNext"
            variant="elevated"
          >
            TAKE THE QUIZ
          </v-btn>
        </div>
      </v-app-bar>
    </div>
  </div>
</template>

<script>
import MethodDataService from "../services/MethodDataService";
import { mapActions, mapGetters } from "vuex";

// Static blue icons matching this method type, ported from the prep-quiz-kit
// design reference (rather than each Firestore method's own admin-set color).
const METHOD_TYPE_ICONS = {
  Pills: "/img/pills.webp",
  Rings: "/img/ring.webp",
};

// The two injection methods are shown as their own separate accordion rows
// (rather than grouped under one "Injections" entry), so each needs its own
// icon — matched by name since they share the same methodType.
const CABOTEGRAVIR_ICON = "/img/cab.webp";
const LENACAPAVIR_ICON = "/img/inject.webp";

export default {
  name: "HomePage",
  data: () => ({
    allMethods: [],
    selectedMethodId: null,
  }),
  async created() {
    console.log(this.$route.query);
    if (
      this.$route.query &&
      this.$route.query.country &&
      ["South Africa", "Kenya"].includes(this.$route.query.country)
    ) {
      await this.$store.dispatch("setCountry", this.$route.query.country);
    } else {
      await this.$store.dispatch("setCountry", "South Africa");
    }
    console.log("You are using the country: " + this.country);

    // Load all methods
    this.allMethods = await MethodDataService.getAll();
    console.log("All Methods:", this.allMethods);
  },
  computed: {
    ...mapGetters(["country"]),
    mainMethods() {
      // Filter methods by country first, then sort by position
      const countryFilteredMethods = this.allMethods
        .filter((method) => {
          if (
            method.applicableCountries &&
            method.applicableCountries.length > 0
          ) {
            return method.applicableCountries.includes(this.country);
          }
          return true;
        })
        .sort((a, b) => (a.position || 0) - (b.position || 0));

      // Injections are shown as their own separate row per method (e.g.
      // CAB-LA vs Lenacapavir), while other types are grouped by methodType.
      const rows = [];
      const seenTypes = new Set();

      countryFilteredMethods.forEach((method) => {
        if (method.methodType === "Injections") {
          rows.push({
            name: method.name,
            id: method.id,
            methods: [method],
            icon: this.iconForMethod(method),
          });
          return;
        }

        if (method.methodType && !seenTypes.has(method.methodType)) {
          seenTypes.add(method.methodType);
          const methodsOfType = countryFilteredMethods.filter(
            (m) => m.methodType === method.methodType,
          );
          rows.push({
            name: method.methodType,
            id: method.methodType,
            methods: methodsOfType,
            icon: this.iconForMethod(method),
          });
        }
      });

      return rows;
    },
    heroMethods() {
      return this.mainMethods.map((group) => ({
        id: group.id,
        label: group.name,
        imageUrl: group.icon,
      }));
    },
  },
  methods: {
    ...mapActions(["setCountry"]),
    iconForMethod(method) {
      if (method.methodType === "Injections") {
        return /cab/i.test(method.name || "")
          ? CABOTEGRAVIR_ICON
          : LENACAPAVIR_ICON;
      }
      return METHOD_TYPE_ICONS[method.methodType] || null;
    },
    sortedProcess(method) {
      if (!method || !method.process) return [];
      return [...method.process].sort(
        (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0),
      );
    },
    selectMethod(methodId) {
      if (this.selectedMethodId === methodId) {
        this.selectedMethodId = null; // Collapse if same method clicked
      } else {
        this.selectedMethodId = methodId;
      }
    },
    openMethod(methodId) {
      this.selectedMethodId = methodId;
      this.$nextTick(() => {
        const target = this.$el.querySelector(`[data-method-id="${methodId}"]`);
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    navigateNext() {
      this.$gtag.event("Navigation to Quiz", {
        event_category: "navigation_to_quiz",
        event_label: "navigation_to_quiz_from_homepage",
        value: "homepage",
      });
      this.$router.push("/QuizPageOne");
    },
  },
};
</script>

<style lang="scss" scoped>
// HomePage specific styles only

.page-container {
  height: 100vh;
  position: relative;
  overflow-y: scroll; // Always reserve the scrollbar gutter so content width stays stable
  overflow-x: hidden;
  background: transparent;
  scrollbar-width: thin; // Firefox — force a persistent, non-overlay scrollbar
  scrollbar-color: rgba(255, 255, 255, 0.7) rgba(255, 255, 255, 0.15);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.15);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 4px;
  }
}

.full-page-background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
}

.background-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 30%;
}

.header-section {
  background: transparent;
  overflow: hidden;
  min-height: auto;
  max-height: none;
  padding-bottom: 24px;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

.header-title {
  text-transform: none;
}

.header-content {
  position: relative;
  z-index: 1;
  padding-top: 40px;

  @media (max-height: 480px) and (orientation: landscape) {
    padding-top: 16px;
  }
}

.hero-method-row {
  display: flex;
  justify-content: center;
  gap: 22px;
  flex-wrap: wrap;
  margin-top: 16px;

  @media (max-width: 480px) {
    gap: 12px;
  }

  @media (max-height: 480px) and (orientation: landscape) {
    margin-top: 8px;
  }
}

.hero-method-chip {
  text-align: center;
  width: 104px;
  cursor: pointer;

  @media (max-width: 480px) {
    width: 76px;
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 3px;
    border-radius: 24px;
  }
}

.hero-method-chip:hover .hero-method-icon-wrap,
.hero-method-chip:focus-visible .hero-method-icon-wrap {
  background: rgba(255, 255, 255, 0.32);
}

.hero-method-icon-wrap {
  width: 100px;
  height: 100px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(8px);
  border: 2px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 8px;

  @media (max-width: 480px) {
    width: 72px;
    height: 72px;
    border-radius: 18px;
  }
  transition: background 0.2s;
}

.hero-method-icon {
  width: 58px;
  height: 58px;
  object-fit: contain;

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
}

.hero-method-label {
  color: #ffffff;
  font-weight: 700;
  font-size: 0.88rem;
  font-family: "Montserrat", sans-serif;
  line-height: 1.2;

  @media (max-width: 480px) {
    font-size: 0.74rem;
  }
}

.content-section {
  flex: 1 0 auto;
  background: transparent;
  border-radius: 0;
  margin: 0;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  overflow: visible; // Let the whole page scroll instead of just this section
}

.content-inner {
  flex: 1 0 auto;
  overflow: visible;
  min-height: auto;
}

.method-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px 32px 120px 32px;
  flex: 1 0 auto;
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
  box-sizing: border-box;
}

.method-item {
  display: flex;
  flex-direction: column;
}

.method-btn {
  background: #ffffff !important;
  color: #3d80e8 !important;
  border: 2px solid #3d80e8 !important;
  border-radius: 16px !important;
  height: 64px !important;
  font-weight: 700 !important;
  font-size: 18px !important;
  text-transform: none !important;
  padding: 0 20px !important;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08) !important;
  width: 100% !important;
  transition: all 0.2s !important;

  @media (max-width: 480px) {
    font-size: 15px !important;
    padding: 0 14px !important;
  }

  @media (max-width: 360px) {
    height: auto !important;
    min-height: 64px !important;
    padding: 10px 12px !important;

    :deep(.v-btn__content) {
      white-space: normal !important;
    }
  }

  :deep(.v-btn__content) {
    width: 100% !important;
  }

  &.active {
    background: #3d80e8 !important;
    color: #ffffff !important;
    border-radius: 16px 16px 0 0 !important;
    box-shadow: 0 4px 16px rgba(61, 128, 232, 0.35) !important;
  }
}

.method-name-group {
  min-width: 0;
  flex: 1 1 auto;
}

.method-icon-chip {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    margin-right: 8px;
  }
}

.method-btn-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.method-text {
  text-align: left;
  min-width: 0;

  @media (max-width: 360px) {
    white-space: normal;
    word-break: break-word;
    line-height: 1.2;
  }
}

.dropdown-icon {
  color: #3d80e8 !important;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  font-size: 20px !important;
}

.method-btn.active .dropdown-icon {
  color: #ffffff !important;
  transform: rotate(180deg);
}

// Smooth transition animations for method expansion
.method-expand-enter-active,
.method-expand-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  overflow: hidden;
}

.method-expand-enter-from,
.method-expand-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-8px);
}

.method-expand-enter-to,
.method-expand-leave-from {
  max-height: 1000px;
  opacity: 1;
  transform: translateY(0);
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
}

.method-content {
  margin-top: 0;
}

.method-details-box {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(0);
  transition: transform 0.2s ease;

  &:first-child {
    border-radius: 0 0 12px 12px;
  }
}

// Method-specific card styling
.method-card-oral-pills {
  border-left: 4px solid #3d80e8;
  background: linear-gradient(135deg, #f8fbff 0%, #e8f4ff 100%);
  box-shadow: 0 4px 12px rgba(61, 128, 232, 0.15);
}

.method-card-the-ring {
  border-left: 4px solid #eba614;
  background: linear-gradient(135deg, #fffbf0 0%, #fff4e0 100%);
  box-shadow: 0 4px 12px rgba(235, 166, 20, 0.15);
}

.method-card-injection {
  border-left: 4px solid #8e44ad;
  background: linear-gradient(135deg, #f8f5ff 0%, #f0e8ff 100%);
  box-shadow: 0 4px 12px rgba(142, 68, 173, 0.15);
}

.method-blurb-item {
  margin-bottom: 0;
}

.method-tagline {
  color: #3d80e8;
  font-weight: 700;
  font-size: 14px;
  margin: 0 0 6px;
}

.method-schedule {
  color: #555;
  font-style: italic;
  font-size: 13px;
  margin: 0 0 12px;
}

.method-process-label {
  color: #333;
  font-weight: 700;
  font-size: 14px;
  margin: 0 0 8px;
}

.method-process-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.method-process-row {
  display: flex;
  gap: 12px;
  padding: 8px 12px;
  background: #f0f6ff;
  border-radius: 8px;
}

.method-process-time {
  color: #3d80e8;
  font-weight: 700;
  font-size: 13px;
  min-width: 100px;
  flex-shrink: 0;
}

.method-process-detail {
  color: #333;
  font-size: 13px;
}

.method-note {
  color: #666;
  font-size: 13px;
  border-left: 3px solid #3d80e8;
  padding-left: 10px;
  margin: 0;
}

.blurb-separator {
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    #ddd 20%,
    #ddd 80%,
    transparent 100%
  );
  margin: 16px 0;
  opacity: 0.6;
}

.background-content {
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 2;
  pointer-events: none;
}

.choice-image {
  width: 100%;
  max-width: 220px;
  height: auto;
  object-fit: contain;
}

// Override global styles for this page
:deep(.v-app-bar) {
  background: transparent !important;
  box-shadow: none !important;
}

// Fixed bottom button styles
.fixed-bottom-buttons {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.bottom-bar {
  border: none !important;
  backdrop-filter: blur(10px);
}

.quiz-button {
  background: #eb63eb !important;
  color: white !important;
  border-radius: 10px !important;
  font-weight: bold !important;
  font-size: 16px !important;
  text-transform: none !important;
  padding: 0 32px !important;
  height: 50px !important;
  min-width: 120px !important;
  box-shadow: 0 4px 12px rgba(235, 99, 235, 0.3) !important;
}

// Ensure expansion panels work properly
:deep(.v-expansion-panel-title) {
  min-height: auto !important;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 0 !important;
}
</style>
